import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '@/core/services/auth/auth.service';
import { NavegacaoService } from '@/core/services/navegacao/navegacao.service';
import { UsuarioLogadoService } from '@/core/services/usuarioLogado/usuario-logado.service';
import { AvatarComponent } from '@/shared/components/ui/avatar';
import { ButtonComponent } from '@/shared/components/ui/button';
import {
  MenuComponent,
  MenuDivisorComponent,
  MenuGatilhoDirective,
  MenuItemComponent,
} from '@/shared/components/ui/menu';

/** Qual controle o botão de menu aciona: gaveta (mobile), sidebar (vertical) ou subnav (horizontal). */
type ModoMenu = 'drawer' | 'sidenav' | 'subnav';

/**
 * Topbar única do shell (padrão C), usada nos dois layouts.
 *
 * O próprio host é a barra (`role="banner"`), para ficar `sticky` como filho direto do layout.
 */
@Component({
  selector: 'app-topnav',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ButtonComponent,
    AvatarComponent,
    MenuComponent,
    MenuGatilhoDirective,
    MenuItemComponent,
    MenuDivisorComponent,
  ],
  host: {
    role: 'banner',
    class:
      'sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between gap-[0.75rem] bg-rfy-surface ' +
      'px-[clamp(0.75rem,2vw,1.5rem)] [border-bottom:1px_solid_var(--rfy-border)]',
  },
  templateUrl: './topnav.component.html',
})
export class TopnavComponent {
  protected readonly navegacao = inject(NavegacaoService);
  private readonly auth = inject(AuthService);
  private readonly token = inject(UsuarioLogadoService).obterUsuarioLogado()?.userToken;

  // Dados do usuário calculados uma vez.
  protected readonly nomeCompleto = [this.token?.nome, this.token?.sobrenome].filter(Boolean).join(' ');
  protected readonly email = this.token?.email ?? '';
  protected readonly foto = this.token?.foto ? `data:image/png;base64,${this.token.foto}` : null;

  private readonly modoMenu = computed<ModoMenu>(() => {
    if (this.navegacao.mobile()) return 'drawer';
    return this.navegacao.layoutEfetivo() === 'vertical' ? 'sidenav' : 'subnav';
  });

  protected readonly rotuloMenu = computed(() => {
    switch (this.modoMenu()) {
      case 'drawer':
        return this.navegacao.drawerAberto() ? 'Fechar menu' : 'Abrir menu';
      case 'sidenav':
        return this.navegacao.sidenavRecolhida() ? 'Expandir menu lateral' : 'Recolher menu lateral';
      default:
        return this.navegacao.subnavVisivel() ? 'Ocultar navegação' : 'Mostrar navegação';
    }
  });

  protected readonly expandidoMenu = computed(() => {
    switch (this.modoMenu()) {
      case 'drawer':
        return this.navegacao.drawerAberto();
      case 'sidenav':
        return !this.navegacao.sidenavRecolhida();
      default:
        return this.navegacao.subnavVisivel();
    }
  });

  protected aoClicarMenu(): void {
    switch (this.modoMenu()) {
      case 'drawer':
        this.navegacao.alternarDrawer();
        break;
      case 'sidenav':
        this.navegacao.alternarSidenav();
        break;
      default:
        this.navegacao.alternarSubnav();
    }
  }

  protected sair(): void {
    this.auth.logout();
  }
}
